/**
 * The org-scale list contract, client side (many-properties-per-org, Phase 2).
 *
 * The API's list endpoints are ADDITIVE: send no paging parameter and you get the legacy
 * bare array; send one and you get `{ items, total, limit, offset }`. And the whole
 * contract sits behind a global feature flag (`ORG_SCALE_PAGINATION`) which is off by
 * default, so a paged REQUEST can still come back as a bare array — that is the flag
 * doing its job, not an error.
 *
 * Which means the portal cannot decide statically which shape it is dealing with. It
 * detects, per response, and degrades to exactly today's behaviour when the server is
 * still serving the legacy shape. That is what lets this ship and deploy weeks before the
 * flag is flipped, and what makes the flip reversible without a frontend deploy.
 */

import { QueryClient } from "react-query";

/** The paged response body. */
export interface Page<T> {
  items: T[];
  /** Total matching rows under the same filters, ignoring limit/offset. */
  total: number;
  limit: number;
  offset: number;
}

export interface ListResult<T> {
  items: T[];
  /** Rows matching the filters. Equals `items.length` on the legacy path. */
  total: number;
  /**
   * True when the SERVER paged, filtered and sorted this result.
   *
   * The caller must branch on this rather than assuming: when false, paging, search and
   * sort are still the client's job, exactly as they are today.
   */
  serverPaged: boolean;
}

export interface PageParams {
  limit?: number;
  offset?: number;
  sort?: string;
  desc?: boolean;
  search?: string;
}

/**
 * Normalise either response shape into one thing.
 *
 * An array is the legacy full response — every matching row, unpaged and unsorted by the
 * server. An envelope is one window of a larger set.
 */
export function unwrapListResponse<T>(response: T[] | Page<T> | undefined): ListResult<T> {
  if (Array.isArray(response)) {
    return { items: response, total: response.length, serverPaged: false };
  }

  if (response && Array.isArray(response.items)) {
    return { items: response.items, total: response.total, serverPaged: true };
  }

  return { items: [], total: 0, serverPaged: false };
}

/**
 * Apply a search the SERVER did not apply.
 *
 * With ORG_SCALE_PAGINATION off — the seeded default, and the state this branch ships
 * into — the server ignores `search` entirely: `parsePageParams` returns undefined and the
 * legacy branch runs `searchProperties({ organisationId })`, which has no search parameter.
 * The response is the whole unfiltered list.
 *
 * Every picker in this branch deleted its client-side filter in the same commit that
 * started sending `search`, so on day one of the deploy the search boxes did nothing: type
 * "Queen" in the global palette and it shows the first eight properties of the
 * organisation, unchanged, whatever you type. Not a visible failure — a dead control that
 * looks alive.
 *
 * So the filter happens here when the server did not do it. It is exactly the filtering
 * these screens did before, and correct, because an unpaged response really is the whole
 * list. Once the flag is on, `serverPaged` is true and this is a no-op.
 */
export function applySearchFallback<T>(
  result: ListResult<T>,
  search: string | undefined,
  searchable: (item: T) => (string | undefined | null)[],
  limit = MAX_PAGE_LIMIT,
): ListResult<T> {

  if (result.serverPaged || !search?.trim()) {
    return result;
  }

  const needle = search.trim().toLowerCase();
  const matches = result.items.filter((item) =>
    searchable(item).some((value) => typeof value === "string" && value.toLowerCase().includes(needle)),
  );

  return {
    // Capped the way the server would have capped it, so a caller comparing
    // `items.length` against `total` still sees "showing 50 of 214".
    items: matches.slice(0, limit),
    // The MATCH count, not the capped one. A caller treating this as one window — every
    // picker in this app — needs it to say "showing 50 of 214, search to narrow".
    //
    // A caller that PAGES this array itself needs the opposite, and must reconcile the two
    // ITSELF rather than changing this: computing totalPages from an uncapped count while
    // holding a capped array renders empty tail pages under a confident total. The members
    // table does that reconciliation — see MEMBER_FALLBACK_LIMIT and the total it reports.
    total: matches.length,
    serverPaged: false,
  };
}

/**
 * Sort a list the SERVER did not sort.
 *
 * The twin of {@link applySearchFallback}, and it exists for the identical reason. With
 * ORG_SCALE_PAGINATION off the server ignores `sort` exactly as it ignores `search`, while
 * the table runs `manualSorting` — so it renders the rows in the order they arrived and
 * paints a sorted-ascending arrow over them. A header that reorders nothing is a dead
 * control that looks alive, which is the failure the search fallback was written to
 * prevent; sort was left out of that fix.
 *
 * Ordered the way MySQL orders it, so flipping the flag does not reshuffle a page: the
 * collation is case-insensitive, NULLs sort first ascending, and ties break on the row's
 * id — which is what stops a local page walk repeating one row and skipping another.
 */
export function applySortFallback<T>(
  result: ListResult<T>,
  sort: string | undefined,
  desc: boolean | undefined,
  sortValue: (item: T, field: string) => string | number | undefined,
  idOf: (item: T) => string,
): ListResult<T> {

  if (result.serverPaged || !sort) {
    return result;
  }

  const direction = desc ? -1 : 1;

  const sorted = [...result.items].sort((a, b) => {
    const left = foldForSort(sortValue(a, sort));
    const right = foldForSort(sortValue(b, sort));

    if (left !== right) {
      // NULLs first ascending, matching the collation's ordering rather than pushing
      // "no value" to whichever end JavaScript happens to choose.
      if (left === undefined || left === null) return -1 * direction;
      if (right === undefined || right === null) return 1 * direction;
      return (left < right ? -1 : 1) * direction;
    }

    // The id tiebreaker is ASCENDING even when the sort is descending: it exists to make
    // the order total, not to be part of what the user asked for.
    return idOf(a).localeCompare(idOf(b));
  });

  return { ...result, items: sorted };
}

/** Case-folded, so the comparator matches the columns' case-insensitive collation. */
function foldForSort(value: string | number | undefined): string | number | undefined {
  return typeof value === "string" ? value.toLowerCase() : value;
}

/**
 * Build the query object for a paged request.
 *
 * Returns `{}` when there is nothing to send, which matters: sending ANY paging parameter
 * opts the request into the envelope, so an accidental `limit=undefined` would flip the
 * response shape for a caller that only wanted the full list.
 *
 * `search` is the one to watch. It is a PAGING parameter on the server side, so sending
 * it alone still returns the envelope, capped at the default page size. Never send it
 * from a caller that wants the whole filtered list back.
 */
export function pageQueryParams(params?: PageParams): Record<string, string> {
  if (!params) {
    return {};
  }

  const query: Record<string, string> = {};

  if (params.limit !== undefined) query.limit = String(params.limit);
  // Sent as a string because the API client drops falsy query values, and offset 0 — the
  // first page, i.e. the common case — is falsy as a number and truthy as "0".
  if (params.offset !== undefined) query.offset = String(params.offset);
  if (params.sort) query.sort = params.sort;
  if (params.desc) query.desc = "true";
  // Only when non-empty: a cleared search box must not send `search=`, which would keep
  // the request on the paged contract for no reason. (The server treats an empty search
  // as "no filter" either way — this just keeps the query string honest.)
  if (params.search) query.search = params.search;

  return query;
}

/** The server's ceiling on `limit`. Asking for more is a 400, not a clamp. */
export const MAX_PAGE_LIMIT = 200;

/**
 * Every row of a list endpoint, fetched in pages.
 *
 * THREE CALLERS, ALL NAMED. This is not a general-purpose helper, and the pickers and
 * tables it used to serve no longer use it — they take a page and send a search term.
 *
 * What is left is the work that is genuinely about the whole organisation:
 *
 *   useGetAllPropertiesForMap             a pin per property; 50 of 1,500 is a wrong map
 *   useGetAllOrganisationPropertyInfoForReport  a report over the portfolio
 *   fetchAllPropertyIds                   an organisation-wide export, AT SUBMIT
 *
 * Before adding a fourth, check it is not one of: a table (take a page), a picker (send a
 * search), a lookup (ask for the ids you have), or a number (ask the API for the number).
 * It trades one enormous request for a bounded number of merely large ones — the payload
 * is unchanged, so it fixes nothing except the 400 under ENFORCE_PAGINATION.
 *
 * Walks by `offset` and stops on `total`, so it returns the same set the unpaged request
 * would. Two behaviours are deliberate:
 *
 *  - A bare array back means the flag is off and the server ignored the paging parameters
 *    entirely — that IS the whole list, so it is returned as-is rather than walked (the
 *    next request would return the same rows again, forever).
 *  - A page that adds no rows ends the walk rather than looping. A server that stops
 *    advancing is a bug, and spinning on it in the browser turns that bug into an outage.
 */
export async function fetchAllPages<T>(
  fetchPage: (params: Record<string, string>) => Promise<T[] | Page<T>>,
  pageSize: number = MAX_PAGE_LIMIT,
): Promise<T[]> {
  const limit = Math.min(pageSize, MAX_PAGE_LIMIT);
  const collected: T[] = [];
  let offset = 0;

  // Bounded so a paging bug surfaces as short data rather than a hung tab. 200 pages at
  // the maximum limit is 40,000 rows — far past the largest organisation.
  for (let guard = 0; guard < 200; guard++) {
    const response = await fetchPage(pageQueryParams({ limit, offset }));
    const { items, total, serverPaged } = unwrapListResponse<T>(response);

    if (!serverPaged) {
      // Flag off: this is the legacy full array.
      return items;
    }

    collected.push(...items);

    if (items.length === 0 || collected.length >= total) {
      break;
    }

    offset += items.length;
  }

  return collected;
}

/**
 * Sort fields the property list endpoints accept. Anything else is a 400.
 *
 * A MIRROR of PROPERTY_SORT_FIELDS in api.tetherme.io
 * (main-api/src/services/property/search.ts) — it must not drift, because a header this
 * list says is sortable sends its name straight through as `?sort=`, and a name the server
 * rejects is a 400 that collapses the whole page.
 *
 * `totalDeviceCount` is not a column of `Installation`: the server orders by it through a
 * subquery, added so the DEVICES column keeps the sort the unpaged table has always had.
 * It needs the API deployed first — see the note on DEFAULT_SERVER_SORT.
 */
export const PROPERTY_SORT_FIELDS = ["address1", "createdAt", "type", "customPropertyId", "totalDeviceCount"] as const;
export type PropertySortField = (typeof PROPERTY_SORT_FIELDS)[number];

export function isPropertySortField(field: string): field is PropertySortField {
  return (PROPERTY_SORT_FIELDS as readonly string[]).includes(field);
}

export const DEFAULT_PAGE_SIZE = 50;

/**
 * The in-flight whole-list request per endpoint, so two pickers mounting together share
 * one download rather than racing.
 *
 * Deliberately holds ONLY a pending promise, and deletes the entry the moment it settles:
 * nothing is cached here. The cached copy lives in the react-query cache under the caller's
 * key, which sign-out clears.
 *
 * THE CALLER'S KEY MUST CARRY THE TENANT. This map is keyed on that key and is a plain
 * module-level object, so no cache lifecycle reaches it — and the in-SPA organisation
 * switch does not even clear the react-query cache, it calls `invalidateQueries()` (see
 * AuthProvider.handleSwitchOrganisation). A tenant-agnostic key therefore lets a request
 * begun in one organisation be awaited by a caller in the next, and lets that response
 * settle after the switch and write itself back in as fresh. `wholeListKey` puts the
 * organisation id in the key for exactly this reason; keep it there.
 */
const inFlightLists = new Map<string, Promise<unknown>>();

/** The cache-key segment that marks a shared flag-off whole list. */
export const WHOLE_LIST_FALLBACK = "wholeListFallback";

/**
 * Fetch a list, sharing the answer when the server gave us the WHOLE list.
 *
 * While ORG_SCALE_PAGINATION is off the server ignores `propertyIds` and `search` alike
 * and answers with the organisation's entire property list — so every caller is narrowing
 * the same array, under a different react-query key each time (`{byIds}`, `{search}`). A
 * device table paging 1→2→3 downloaded 1,500 properties three times; every settled
 * keystroke in a picker did it again. Before this branch these screens shared one cached
 * fetch, so without this the degraded path costs far more than what it replaced.
 *
 * Shared through the QUERY CACHE rather than a module variable, so it is subject to the
 * same lifetime as everything else: cleared on sign-out and organisation switch, dropped
 * when a property mutation invalidates QueryKeys.PROPERTY, and garbage-collected once
 * nothing references it.
 */
export async function fetchListSharingFallback<T>(
  queryClient: QueryClient,
  key: unknown[],
  fetchIt: () => Promise<T[] | Page<T>>,
): Promise<T[] | Page<T>> {
  const endpoint = JSON.stringify(key);

  // `isInvalidated` matters: a property create/update/delete invalidates QueryKeys.PROPERTY,
  // and reading the data anyway would answer the refetch it triggered from the stale list.
  const state = queryClient.getQueryState<T[]>(key);
  if (state?.data && !state.isInvalidated) {
    return state.data;
  }

  const pending = inFlightLists.get(endpoint) as Promise<T[] | Page<T>> | undefined;
  if (pending) {
    return await pending;
  }

  // THE ENTRY HAS TO EXIST BEFORE THE REQUEST DOES, or an invalidation cannot mark it.
  //
  // The check above is the whole guard against answering a mutation's own refetch from the
  // list it just changed — but it only works once the entry is there. On the FIRST fetch it
  // is not: a property create, update or delete lands mid-flight, `invalidateQueries`
  // matches nothing under this key, and the pre-mutation response is then written in below
  // as fresh. Every later caller reads a list missing the property that was just created.
  //
  // Building it up front costs nothing — no data, so the fast path above still skips it —
  // and gives `invalidateQueries(QueryKeys.PROPERTY)` something to mark, because this key
  // is prefixed with it.
  queryClient.getQueryCache().build(queryClient, { queryKey: key });

  const request = fetchIt();
  inFlightLists.set(endpoint, request);

  try {
    const response = await request;
    const { items, serverPaged } = unwrapListResponse<T>(response);
    // Re-read rather than reusing the state from above: this is the state AFTER the
    // request, and what is being asked is whether anything invalidated it while it ran.
    if (!serverPaged && !queryClient.getQueryState<T[]>(key)?.isInvalidated) {
      queryClient.setQueryData(key, items);
    }
    return response;
  } finally {
    // Always — a rejected request must not be replayed to every later caller.
    inFlightLists.delete(endpoint);
  }
}
